import { ApplicationAPI } from '../../../types/salesforce/api/application/application-api';
import { ApplicationDTO } from '../../../types/salesforce/dto/application/application-dto';
import { AccountAPI } from '../../../types/salesforce/api/application/account-api';
import { AccountDTO } from '../../../types/salesforce/dto/application/account-dto';
import { AddressAPI } from '../../../types/salesforce/api/application/address-api';
import { ContactDTO } from '../../../types/salesforce/dto/application/contact-dto';
import { ContactAPI } from '../../../types/salesforce/api/application/contact-api';
import { PaymentStatus } from '../../../types/application/payment';
import { PaymentAPI } from '../../../types/salesforce/api/application/payment-api';
import { OpportunityDTO } from '../../../types/salesforce/dto/application/opportunity-dto';
import { DeliveryDTO } from '../../../types/salesforce/dto/application/delivery-dto';
import {
  AddressTypeAPI,
  DeliveryAPI,
} from '../../../types/salesforce/api/application/delivery-api';
import { StageAPI } from '../../../types/salesforce/api/application/stage-api';
import { DirectorDTO } from '../../../types/salesforce/dto/application/director-dto';
import {
  DirectorAPI,
  DirectorDetailsAPI,
  IdTypeAPI,
} from '../../../types/salesforce/api/application/director-details-api';
import { DirectorIDType } from '../../../types/salesforce/dto/application/director-id-type-dto';
import { getDateFromDTODate } from './get-date-from-dto-date';
import { BankDetailsDto } from '../../../types/salesforce/dto/application/bank-details-dto';
import { BankDetailsApi } from '../../../types/salesforce/api/application/bank-details-api';
import { BankAccountTypeAPI } from '../../../types/salesforce/api/application/bank-account-type-api';
import {
  BankAccountHolderTypeDTO,
  BankAccountTypeDTO,
  BavStatusDTO,
  BusinessRegistrationTypeDTO,
} from './bank-details-types';
import { BankAccountHolderTypeAPI } from '../../../types/salesforce/api/application/bank-account-holder-type-api';
import { BusinessRegistrationTypeApi } from '../../../types/salesforce/api/application/business-registration-type-api';
import { BavStatusApi } from '../../../types/salesforce/api/application/bav-status-api';
import { AddressTypeDTO } from './address-types';
import { RemotePaymentAPI } from 'src/features/common/types/salesforce/api/application/remote-payment-api';
import { IdentityTypeDirectors, SolutionType } from 'src/features/common/types';
import { SOUTH_AFRICA } from 'src/features/common/helpers/countries';
import { StepNames } from './step-names';
import { LAST_NAME_PLACEHOLDER } from 'src/helpers/lock-field-mappings';
import { getCardMachinesDTO } from 'src/features/common/helpers/dto-mappers/salesforce/get-card-machines-api-from-dto';

export function getApplicationAPIFromDTO(applicationDTO: ApplicationDTO): ApplicationAPI {
  if (applicationDTO.lead?.lead?.solutionType === SolutionType.vodacomBusiness) {
    applicationDTO = getVodacomBusinessApplication(applicationDTO);
  }

  const BUSINESS_NAME_PLACEHOLDER = 'Company Placeholder';

  return {
    solutionType: applicationDTO.opportunity?.solutionType,
    referenceNumber: applicationDTO.opportunity?.reference_no,
    remotePayment: getRemotePaymentAPI(applicationDTO),
    account: getAccountAPI(applicationDTO.account),
    cardFees: [],
    contact: getContactAPI(applicationDTO.contact),
    deliveries: (applicationDTO.opportunity?.deliveries || []).flatMap(getDeliveryAPI),
    bankDetails: getBankDetailsAPI(applicationDTO.bank_account),
    payment: getPaymentAPI(applicationDTO.opportunity),
    productsOrdered: [],
    stage: getStageAPI(applicationDTO),
    directors: getDirectorDetailsAPI(applicationDTO.account?.directors),
    categories: applicationDTO.categories,
    applicationKilled: applicationDTO?.opp_kill_flag,
    brokerLogin:
      applicationDTO.opportunity?.dealerId ||
      (applicationDTO.opportunity?.solutionType === SolutionType.vodacomBusiness
        ? ''
        : applicationDTO.lead?.lead?.dealerId),
    agentId: applicationDTO.opportunity?.agentId || applicationDTO.lead?.lead?.agentId,
    locked_fields: applicationDTO.lead?.locked_fields,
    cardMachines: getCardMachinesDTO(applicationDTO.opportunity?.deliveries || []),
  } as ApplicationAPI;

  function getStageAPI(applicationDTO: ApplicationDTO): StageAPI | undefined {
    return {
      stepName: applicationDTO.step_name || undefined,
      subStepName: applicationDTO.substep_name || undefined,
    } as StageAPI;
  }

  function getRemotePaymentAPI(applicationDTO: ApplicationDTO): RemotePaymentAPI | undefined {
    return {
      responseCode: applicationDTO.opportunity?.response_code,
      responseMessage: applicationDTO.opportunity?.response_message,
      transactionId: applicationDTO.opportunity?.payment_transaction_id,
    } as RemotePaymentAPI;
  }

  function getBankAccountTypeAPI(bankAccountTypeDto?: string): BankAccountTypeAPI | undefined {
    if (bankAccountTypeDto) {
      switch (bankAccountTypeDto) {
        case BankAccountTypeDTO.CURRENT_CHEQUE:
          return BankAccountTypeAPI.CHEQUE_CURRENT;
        case BankAccountTypeDTO.SAVINGS:
          return BankAccountTypeAPI.SAVINGS;
        default:
          throw new Error(`Integration revamp: unknown bank account type: ${bankAccountTypeDto}`);
      }
    }
  }

  function getBankAccountHolderTypeAPI(
    bankAccountHolderTypeDto?: string,
  ): BankAccountHolderTypeAPI | undefined {
    if (bankAccountHolderTypeDto) {
      switch (bankAccountHolderTypeDto) {
        case BankAccountHolderTypeDTO.BUSINESS:
          return BankAccountHolderTypeAPI.BUSINESS;
        case BankAccountHolderTypeDTO.PERSONAL:
          return BankAccountHolderTypeAPI.PERSONAL;
        default:
          throw new Error(
            `Integration revamp: unknown bank account holder type: ${bankAccountHolderTypeDto}`,
          );
      }
    }
  }

  function getBusinessRegistrationTypeAPI(
    businessRegistrationTypeDto?: string,
  ): BusinessRegistrationTypeApi | undefined {
    if (businessRegistrationTypeDto) {
      switch (businessRegistrationTypeDto) {
        case BusinessRegistrationTypeDTO.BUSINESS_REGISTRATION:
          return BusinessRegistrationTypeApi.BUSINESS_REGISTRATION;
        case BusinessRegistrationTypeDTO.RSA_ID:
          return BusinessRegistrationTypeApi.RSA_ID;
        default:
          throw new Error(
            `Integration revamp: unknown business registration type dto: ${businessRegistrationTypeDto}`,
          );
      }
    }
  }

  function getBankAccountOpenSinceAPI(
    bankAccountOpenYears?: string,
    bankAccountOpenMonths?: string,
  ): Date | undefined {
    if (bankAccountOpenMonths && bankAccountOpenYears) {
      const month: number = parseInt(bankAccountOpenMonths) - 1;
      const year: number = parseInt(bankAccountOpenYears);

      if ((!month && month !== 0) || !year) {
        throw new Error(
          `Integration revamp: unknown number of months or years: years: ${bankAccountOpenYears}, months: ${bankAccountOpenMonths}`,
        );
      }

      return new Date(year, month);
    }
  }

  function getBavStatusAPI(bavStatusDTO?: string): BavStatusApi | undefined {
    if (bavStatusDTO) {
      switch (bavStatusDTO) {
        case BavStatusDTO.INVALID:
          return BavStatusApi.INVALID;
        case BavStatusDTO.VALID:
          return BavStatusApi.VALID;
        default:
          throw new Error(`Inegration revamp: unknown bav status dto: ${bavStatusDTO}`);
      }
    }
  }

  function getBankDetailsAPI(bankDetailsDto?: BankDetailsDto): BankDetailsApi | undefined {
    if (bankDetailsDto) {
      const bankAccountHolderType = getBankAccountHolderTypeAPI(
        bankDetailsDto.bank_account_holder_type,
      );

      const bankDetailsApi = {
        accountType: getBankAccountTypeAPI(bankDetailsDto.account_type),
        bankAccountHolder: bankDetailsDto.bank_account_holder,
        bankAccountHolderType: bankAccountHolderType,
        bankAccountOpenSince: getBankAccountOpenSinceAPI(
          bankDetailsDto.bank_account_open_years,
          bankDetailsDto.bank_account_open_months,
        ),
        bankAccountNumber: bankDetailsDto.bank_account_number,
        bankName: bankDetailsDto.bank_name,
        branchName: bankDetailsDto.branch_name,
        branchCode: bankDetailsDto.branch_code,
        debitOrderDate: bankDetailsDto.debit_order_date,
        bavStatus: getBavStatusAPI(bankDetailsDto.bav_status),
      } as BankDetailsApi;

      switch (bankAccountHolderType) {
        case undefined: // credit vetting not done
        case BankAccountHolderTypeAPI.PERSONAL:
          bankDetailsApi.bankAccountHolderIdNumber = bankDetailsDto.bank_account_holder_id_number;
          break;

        case BankAccountHolderTypeAPI.BUSINESS:
          const businessRegistrationType = getBusinessRegistrationTypeAPI(
            bankDetailsDto.business_registration_type,
          );
          bankDetailsApi.businessRegistrationType = businessRegistrationType;

          switch (businessRegistrationType) {
            case BusinessRegistrationTypeApi.RSA_ID:
              bankDetailsApi.bankAccountHolderIdNumber =
                bankDetailsDto.bank_account_holder_id_number;
              break;
            case BusinessRegistrationTypeApi.BUSINESS_REGISTRATION:
              bankDetailsApi.businessRegistrationNumber =
                bankDetailsDto.business_registration_number ||
                bankDetailsDto.bank_account_holder_id_number;
              break;
            default:
              throw new Error(
                `Integration revamp: unknown business registration type api: ${businessRegistrationType}`,
              );
          }
          break;

        default:
          throw new Error(
            `Integration revamp: unknown bank account holder type: ${bankAccountHolderType}`,
          );
      }

      return bankDetailsApi;
    }
  }

  function getDeliveryAPI(deliveryDTO?: DeliveryDTO): DeliveryAPI | undefined {
    if (deliveryDTO) {
      return {
        address: {
          addressType:
            deliveryDTO.address_type === AddressTypeDTO.BUSINESS
              ? AddressTypeAPI.BUSINESS
              : AddressTypeAPI.OPERATIONAL,
          street: deliveryDTO.street,
          complexName: deliveryDTO.complex,
          suburb: deliveryDTO.suburb,
          city: deliveryDTO.city,
          province: deliveryDTO.region || deliveryDTO.state,
          country: deliveryDTO.country,
          postalCode: deliveryDTO.zip_code,
        },
        price: deliveryDTO.price,
        delivery_option: deliveryDTO.delivery_option,
        paymentMethodCode: deliveryDTO.payment_method_code,
        recipient: {
          name: deliveryDTO.manager_name,
          tradingAs: deliveryDTO.trading_as,
          idNumber: deliveryDTO.id_number,
          phoneNumberWork: deliveryDTO.phone_work,
          phoneNumberCell: deliveryDTO.phone_cell,
          phoneNumber: deliveryDTO.phone,
        },
        order: {
          quantity: deliveryDTO.number_of_devices,
          deviceName: deliveryDTO.device_name,
          deviceId: deliveryDTO.device_id,
          acquiringChannel: deliveryDTO.acquiring_channel,
          isProductCategory: deliveryDTO.is_product_category,
        },
      } as DeliveryAPI;
    }
  }

  function getDirectorAPI(directorDTO: DirectorDTO): DirectorAPI {
    const directorAPI = {
      title: directorDTO.title,
      initials: directorDTO.initials,
      firstName: directorDTO.first_name,
      surname: directorDTO.surname,
      idNumber: directorDTO.directors_id_number,
    } as DirectorAPI;

    switch (directorDTO.id_type) {
      case DirectorIDType.SA_ID:
        directorAPI.idType = IdTypeAPI.RSA_ID;
        break;
      case DirectorIDType.PASSPORT:
        directorAPI.idType = IdTypeAPI.PASSPORT;
        directorAPI.dateOfBirth = getDateFromDTODate(directorDTO.date_of_birth);
        directorAPI.gender = directorDTO.gender;
        directorAPI.passportExpiryDate = getDateFromDTODate(directorDTO.passport_expiry_date);
        directorAPI.countryOfOrigin = directorDTO.country_of_origin;
        break;
      case DirectorIDType.REFUGEE:
        directorAPI.idType = IdTypeAPI.REFUGEE;
        directorAPI.dateOfBirth = getDateFromDTODate(directorDTO.date_of_birth);
        directorAPI.gender = directorDTO.gender;
        directorAPI.passportExpiryDate = getDateFromDTODate(directorDTO.passport_expiry_date);
        directorAPI.countryOfOrigin = directorDTO.country_of_origin;
        break;
      case DirectorIDType.ASYLUM_SEEKER:
        directorAPI.idType = IdTypeAPI.ASYLUM_SEEKER;
        directorAPI.dateOfBirth = getDateFromDTODate(directorDTO.date_of_birth);
        directorAPI.gender = directorDTO.gender;
        directorAPI.passportExpiryDate = getDateFromDTODate(directorDTO.passport_expiry_date);
        directorAPI.countryOfOrigin = directorDTO.country_of_origin;
        break;
      default:
        throw new Error(`Integration revamp: unknown id type: ${directorDTO.id_type}`);
    }

    return directorAPI;
  }

  function getDirectorDetailsAPI(directorsDTO?: DirectorDTO[]): DirectorDetailsAPI | undefined {
    if (directorsDTO) {
      return {
        directors: directorsDTO.map(getDirectorAPI),
      } as DirectorDetailsAPI;
    }
  }

  function getAccountAPI(accountDTO?: AccountDTO): AccountAPI | undefined {
    if (accountDTO) {
      const businessAddress: AddressAPI = {};
      businessAddress.street = accountDTO.street;
      businessAddress.complexName = accountDTO.Complex_Building;
      businessAddress.suburb = accountDTO.suburb;
      businessAddress.city = accountDTO.city;
      businessAddress.province = accountDTO.region;
      businessAddress.country = accountDTO.country;
      businessAddress.postalCode = accountDTO.zip_code;

      /* if monthly turnover isn't set, and the business name is "Company Placeholder" then
         ignore it, as it was probably set by Salesforce and not by the user
       */
      const businessName = accountDTO.business_name;
      const ignoreBusinessNameValue =
        businessName === BUSINESS_NAME_PLACEHOLDER && !accountDTO.monthly_card_turnover;

      return {
        businessType: accountDTO.business_type,
        businessName: ignoreBusinessNameValue ? undefined : accountDTO.business_name,
        businessRegistrationNumber: accountDTO.registration_number,
        industry: accountDTO.industry,
        natureOfBusiness: accountDTO.nature_of_business,
        mcc: accountDTO.mcc,
        isBusinessKnownByDifferentName: accountDTO.trading_name_different_to_business_name,
        businessWebsiteURL: accountDTO.business_website,
        isCreditVettingConsentGiven: accountDTO.business_credit_vetting_consent,
        businessDateOfRegistration: getDateFromDTODate(accountDTO.company_date_of_registration),
        monthlyTurnover: accountDTO.monthly_card_turnover,
        isMonthlyCardTurnoverUnknown: accountDTO.dont_know_how_much_i_will_process,

        businessTradingAsName: accountDTO.trading_name_different_to_business_name
          ? accountDTO.trading_as
          : accountDTO.business_name,

        businessAddress: businessAddress,
        pricingTier: accountDTO.pricing_tier,
      } as AccountAPI;
    }
  }

  function getContactAPI(contactDTO?: ContactDTO): ContactAPI | undefined {
    if (contactDTO) {
      const contactAPI: ContactAPI = {
        firstNames: contactDTO.first_names,
        surname: contactDTO.surname,
        emailAddress: contactDTO.email,
        phoneNumber: contactDTO.phone,
        idType: contactDTO.id_type,
        idNumber: contactDTO.id_number,
        title: contactDTO.title,
        initials: contactDTO.initials,
        gender: contactDTO.gender,
        countryOfOrigin: contactDTO.country_of_origin,
        passportExpiryDate: getDateFromDTODate(contactDTO.passport_expiry_date),
        dateOfBirth: getDateFromDTODate(contactDTO.date_of_birth),
        maritalStatus: contactDTO.marital_status,
        consumerType: contactDTO.consumer_type,
        contactPhoto: contactDTO.contact_photo,
        contactPhotoName: contactDTO.photo_file_name || 'selfie.jpg',
        isResidentialAddressSameAsBusinessAddress:
          !!contactDTO.residential_address_same_as_business_address,
        residentialAddressType: contactDTO.residence,
        residentialAddressPeriodFrom: getDateFromDTODate(contactDTO.period_at_current_address),
      };

      const residentialAddress: AddressAPI = {};
      residentialAddress.street = contactDTO.residential_street;
      residentialAddress.complexName = contactDTO.residential_complex_building;
      residentialAddress.suburb = contactDTO.residential_suburb;
      residentialAddress.city = contactDTO.residential_city;
      residentialAddress.province = contactDTO.residential_province;
      residentialAddress.country = contactDTO.residential_country;
      residentialAddress.postalCode = contactDTO.residential_zip_code;

      contactAPI.residentialAddress = residentialAddress;

      return contactAPI;
    }
  }

  function getPaymentAPI(opportunityDTO?: OpportunityDTO): PaymentAPI | undefined {
    if (opportunityDTO) {
      return {
        orderNumber: opportunityDTO.online_payment_order_no,
        isPaymentSuccessful:
          opportunityDTO.online_payment_order_no ||
          opportunityDTO.solutionType === SolutionType.vodacomBusiness
            ? PaymentStatus.PAYMENT_SUCCESSFUL
            : undefined,
      } as PaymentAPI;
    }
  }
}

function getVodacomBusinessApplication(applicationDTO: ApplicationDTO): ApplicationDTO {
  const lead = applicationDTO.lead?.lead;

  return {
    opportunity: {
      solutionType: lead?.solutionType,
      dealerId: lead?.dealerId,
      deliveries: [
        {
          acquiring_channel: lead?.solutionType,
          city: undefined,
          country: SOUTH_AFRICA,
          device_id: undefined,
          device_name: lead?.device_type,
          gps_location: undefined,
          id_number: lead?.id_number,
          manager_name: lead?.first_names,
          number_of_devices: lead?.device_quantity,
          phone: lead?.mobile_number,
          phone_cell: lead?.mobile_number,
          phone_work: lead?.mobile_number,
          price: 0,
          region: lead?.region,
          state: lead?.region,
          street: lead?.street,
          suburb: lead?.suburb,
          trading_as: lead?.trading_as,
          zip_code: lead?.zip_code,
          serial_numbers: [],
          friendlyTerminalName: [],
          payment_method_code: '',
        },
      ],
    },
    contact: {
      first_names: lead?.first_names,
      surname: LAST_NAME_PLACEHOLDER,
      email: lead?.email,
      phone: lead?.mobile_number,
      id_type: IdentityTypeDirectors.RSA_ID,
      id_number: lead?.id_number,
      country_of_origin: SOUTH_AFRICA,
    },
    account: {
      trading_name_different_to_business_name: true,
      trading_as: lead?.trading_as,
      business_type: lead?.type_of_business,
      region: lead?.region,
      street: lead?.street,
      suburb: lead?.suburb,
      zip_code: lead?.zip_code,
      monthly_card_turnover: lead?.monthly_card_turnover,
    },
    bank_account: {},
    step_name: StepNames.YOUR_BUSINESS,
    substep_name: StepNames.DASHBOARD,
    lead: { lead, locked_fields: applicationDTO.lead?.locked_fields },
  } as ApplicationDTO;
}
